/**
 * Compare – current user vs selected athlete (PRs side by side).
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { getProfile } from '../../domain/usecases/profile';
import { getUserWorkouts } from '../../domain/usecases/workouts';
import Avatar from '../../shared/ui/Avatar.jsx';
import Card from '../../shared/ui/Card.jsx';
import Stat from '../../shared/ui/Stat.jsx';
import Body from '../../shared/ui/Body.jsx';
import Display from '../../shared/ui/Display.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import styles from './ComparePage.module.css';

export function ComparePage() {
  const { userId: otherUserId } = useParams();
  const { userId: myId } = useAuth();
  const [myProfile, setMyProfile] = useState(null);
  const [otherProfile, setOtherProfile] = useState(null);
  const [myWorkouts, setMyWorkouts] = useState([]);
  const [otherWorkouts, setOtherWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!myId || !otherUserId || myId === otherUserId) {
      setLoading(false);
      return;
    }
    Promise.all([
      getProfile(myId),
      getProfile(otherUserId),
      getUserWorkouts(myId, 20),
      getUserWorkouts(otherUserId, 20),
    ])
      .then(([me, other, myW, otherW]) => {
        if (!cancelled) {
          setMyProfile(me);
          setOtherProfile(other);
          setMyWorkouts(myW);
          setOtherWorkouts(otherW);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [myId, otherUserId]);

  if (myId === otherUserId) {
    return (
      <div className={styles.page}>
        <Body tone="secondary" style={{ marginBottom: '1rem' }}>Select a different athlete to compare.</Body>
        <Link to="/compare/select" className={styles.link}>Choose athlete</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton height={200} radius="var(--radius-md)" />
      </div>
    );
  }

  if (!myProfile || !otherProfile) {
    return (
      <div className={styles.page}>
        <Body tone="secondary">Profile not found.</Body>
      </div>
    );
  }

  const myPrs = myProfile.stats || {};
  const otherPrs = otherProfile.stats || {};
  const liftLabels = [
    { key: 'squatPr', label: 'Squat' },
    { key: 'benchPr', label: 'Bench' },
    { key: 'deadliftPr', label: 'Deadlift' },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Link to="/compare/select" className={styles.back}>← Compare with another</Link>
        <Display as="h1" size="l">Compare</Display>
      </div>

      <div className={styles.grid}>
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <Avatar
              src={myProfile.avatarUrl || undefined}
              initials={myProfile.displayName?.charAt(0)?.toUpperCase() || 'Y'}
              size="lg"
            />
            <Display as="h2" size="l" style={{ fontSize: 20, lineHeight: '24px', marginTop: '0.5rem', marginBottom: '0.25rem' }}>
              {myProfile.displayName || 'You'}
            </Display>
            <Link to="/profile" className={styles.profileLink}>View profile</Link>
          </div>
          <div className={styles.stats}>
            {liftLabels.map(({ key, label }) => (
              <Stat
                key={key}
                label={label}
                value={myPrs[key] != null ? `${myPrs[key]} kg` : '—'}
                className={styles.statItem}
              />
            ))}
          </div>
        </Card>

        <div className={styles.vs}>VS</div>

        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <Avatar
              src={otherProfile.avatarUrl || undefined}
              initials={otherProfile.displayName?.charAt(0)?.toUpperCase() || '?'}
              size="lg"
            />
            <Display as="h2" size="l" style={{ fontSize: 20, lineHeight: '24px', marginTop: '0.5rem', marginBottom: '0.25rem' }}>
              {otherProfile.displayName || 'Athlete'}
            </Display>
            <Link to={`/profile/${otherUserId}`} className={styles.profileLink}>View profile</Link>
          </div>
          <div className={styles.stats}>
            {liftLabels.map(({ key, label }) => (
              <Stat
                key={key}
                label={label}
                value={otherPrs[key] != null ? `${otherPrs[key]} kg` : '—'}
                className={styles.statItem}
              />
            ))}
          </div>
        </Card>
      </div>

      <section className={styles.section}>
        <Body tone="secondary" as="h2" style={{ fontWeight: 600, marginBottom: '0.75rem' }}>
          Recent lifts
        </Body>
        <div className={styles.twoCol}>
          <div>
            <Body tone="secondary" as="h3" style={{ fontSize: 13, marginBottom: '0.5rem' }}>
              {myProfile.displayName || 'You'}
            </Body>
            <ul className={styles.liftList}>
              {myWorkouts.length === 0 ? (
                <li><Body tone="secondary" style={{ fontSize: 13 }}>No recent lifts</Body></li>
              ) : myWorkouts.slice(0, 5).map((w) => (
                <li key={w.id} className={styles.liftItem}>
                  <Body style={{ fontSize: 13 }}>{w.exercise} {w.weight} kg × {w.reps}</Body>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Body tone="secondary" as="h3" style={{ fontSize: 13, marginBottom: '0.5rem' }}>
              {otherProfile.displayName || 'Athlete'}
            </Body>
            <ul className={styles.liftList}>
              {otherWorkouts.length === 0 ? (
                <li><Body tone="secondary" style={{ fontSize: 13 }}>No recent lifts</Body></li>
              ) : otherWorkouts.slice(0, 5).map((w) => (
                <li key={w.id} className={styles.liftItem}>
                  <Body style={{ fontSize: 13 }}>{w.exercise} {w.weight} kg × {w.reps}</Body>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
